import { useFormik } from "formik";
import ModalComponent from "..";
import { useUpdateProfileMutation } from "../../../redux/features/auth/auth.api";
import { AuthProfile } from "../../../redux/features/auth/auth.types";
import { ModalCompProps } from "../../../utils/types";
import { AddSocialsSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import { Icon } from "../../icon/icon";
import { TextInput } from "../../inputs/textInput";
import { MiniHeader } from "../../typography/miniHeader";

export const AddSocialsModal = ({
  open,
  handleClose,
  profile,
  onSuccess,
}: ModalCompProps & {
  profile: AuthProfile;
  onSuccess: () => void;
}) => {
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    handleChange,
    resetForm,
  } = useFormik({
    initialValues: {
      facebook: profile?.facebookLink ?? "",
      twitter: profile?.twitterLink ?? "",
      instagram: profile?.instagramLink ?? "",
      tiktok: profile?.tiktokLink ?? "",
    },
    enableReinitialize: true,
    validationSchema: AddSocialsSchema,
    onSubmit: async (vals) => {
      const resp = await updateProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        twitterLink: vals.twitter || undefined,
        linkedinLink: undefined,
        instagramLink: vals.instagram || undefined,
        tiktokLink: vals.tiktok || undefined,
        facebookLink: vals.facebook || undefined,
        website: profile?.website || undefined,
        isOrganisation: profile.isOrganisation,
        organisation: {
          name: profile.organisation?.name ?? "",
          email: profile.organisation?.email ?? "",
          logo: profile.organisation?.logo || undefined,
          phoneNumber: profile.organisation?.phoneNumber || undefined,
        },
      });

      if (resp.data?.status === "success") {
        onSuccess();
        resetForm();
      }
    },
  });

  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        handleClose();
        resetForm();
      }}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader={false}
      title={undefined}
      subtitle={undefined}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Icon svg={"socials"} width={104} height={32} className="" />

        <div>
          <MiniHeader
            header={`Add Social Links`}
            subheader="Facebook, Twitter(X), Instagram, and Tiktok."
          />
        </div>

        <TextInput
          type="text"
          value={values.facebook}
          hasTitle
          title="Facebook"
          placeholder="https://facebook.com/yourhandle"
          onBlur={handleBlur}
          onChange={handleChange}
          name="facebook"
          readOnly={false}
          errors={
            touched.facebook && errors.facebook ? errors.facebook : undefined
          }
        />

        <TextInput
          type="text"
          value={values.twitter}
          hasTitle
          title="Twitter (X)"
          placeholder="https://x.com/yourhandle"
          onBlur={handleBlur}
          onChange={handleChange}
          name="twitter"
          readOnly={false}
          errors={
            touched.twitter && errors.twitter ? errors.twitter : undefined
          }
        />

        <TextInput
          type="text"
          value={values.instagram}
          hasTitle
          title="Instagram"
          placeholder="https://instagram.com/yourhandle"
          onBlur={handleBlur}
          onChange={handleChange}
          name="instagram"
          readOnly={false}
          errors={
            touched.instagram && errors.instagram ? errors.instagram : undefined
          }
        />

        <TextInput
          type="text"
          value={values.tiktok}
          hasTitle
          title="Tiktok"
          placeholder="https://tiktok.com/yourhandle"
          onBlur={handleBlur}
          onChange={handleChange}
          name="tiktok"
          readOnly={false}
          errors={touched.tiktok && errors.tiktok ? errors.tiktok : undefined}
        />

        <div className="flex w-full items-center justify-end gap-2">
          <Button
            text={"Cancel"}
            type="button"
            onClick={() => {
              handleClose();
              resetForm();
            }}
            variation="plain-grey"
            className="max-w-fit"
          />
          <Button
            text={"Add Links"}
            type="submit"
            onClick={() => {}}
            className="max-w-fit"
            loading={isLoading}
            disabled={isLoading}
            variation={"primary"}
          />
        </div>
      </form>
    </ModalComponent>
  );
};
